/* eslint-disable @next/next/no-img-element */
import React from 'react';

import slugify from 'react-slugify';
import { highlightLowest, highlightHighest } from '../../helper/vehicle_comparison';
import { formatCurrency, formatRange, formatTrunk } from '../../helper/vehicle_formatter';
import { event } from '../../lib/gtag';

import styles from './styles.module.scss'
import YesCircle from '../../public/assets/icons/yes-circle.svg'
import NoCircle from '../../public/assets/icons/no-circle.svg'
import { useIsBoiWebsite } from "../Context/WebsiteType";

interface IComparisonTable {
  vehicles: any[],
  mobile: boolean | undefined,
}

const tableRows = [
  { label: 'Make', key: 'make', compareFunction: null },
  { label: 'Model', key: 'model', compareFunction: null },
  { label: 'Variant', key: 'trim_level', compareFunction: null },
  { label: 'Body Type', key: 'body_type', compareFunction: null },
  { label: 'Fuel', key: 'fuel_type', compareFunction: null },
  { label: 'Price', key: 'price', compareFunction: highlightLowest },
  { label: 'Range', subHeader: "(up to)", key: 'range', compareFunction: highlightHighest },
  { label: 'Towing Capacity', key: 'towing_capacity', compareFunction: highlightHighest },
  { label: 'Trunk Capacity', key: 'trunk_capacity', compareFunction: highlightHighest },
  { label: 'Power', key: 'maximum_power_hp', compareFunction: highlightHighest },
  { label: 'Seats', key: 'seating_capacity', compareFunction: highlightHighest },
  { label: 'Battery', key: 'battery_kwh', compareFunction: highlightHighest },
  { label: 'Acceleration', subHeader: '(0-100km/h)', key: 'acceleration', compareFunction: highlightLowest },
  { label: 'Fast Charge', key: 'is_fast_charge', compareFunction: null },
  { label: '', key: 'enquire', compareFunction: null },
]

export default function ComparisonTable({ vehicles, mobile }: IComparisonTable) {
  const all_vehicles = [...vehicles]
  const vehiclesLimit = mobile ? 2 : 4;

  const isBoiWebsite = useIsBoiWebsite();

  // adding empty vehicles
  while (all_vehicles.length < vehiclesLimit) {
    all_vehicles.push([])
  }

  const renderValue = (value: number, type = '') => {
    return `${value > 0 ? `${value}${type}` : '-'}`
  }

  const openInNewTab = (url: string) => {
    window.open(url, '_blank')
  }

  const getValue = (vehicle: any, key: string) => {
    let value: any;
    
    switch (key) {
      case 'towing_capacity':
        value = renderValue(vehicle[key], 'kg')
        break;

      case 'trunk_capacity':
        value = `${formatTrunk(vehicle[key], vehicle.body_type)}`
        break; 

      case 'maximum_power_hp':
        value = renderValue(vehicle[key], 'HP')
        break;

      case 'battery_kwh':
        value = renderValue(vehicle[key], 'kWh')
        break;

      case 'acceleration':
        value = renderValue(vehicle[key], ' seconds')
        break;

      case 'price':
        value = formatCurrency(vehicle[key]);
        break;
        
      case 'range':
        value = renderValue(vehicle[key], `km ${formatRange(vehicle.fuel_type)}`)
        break;

      case 'is_fast_charge':
        const image = vehicle[key] === 0 ? NoCircle.src : YesCircle.src;
        value = <img
          src={image}
          alt={`The vehicle is ${vehicle[key] === 0 ? " not " : ""} Fast Charge`}
        />
        break;

      case 'fuel_type':
        value = vehicle.fuel_type === "BEV" ? "All-Electric" :
          vehicle.fuel_type === "PHEV" ? "Plug-in Hybrid" : vehicle.fuel_type
        break;

      case 'enquire':
        const { make, model } = vehicle;
        
        const base_url = '/vehicle'
        const url = `${base_url}/option/${slugify(make)}/${slugify(model)}/${vehicle.third_party.variantId}`;

        value = (
          <button 
            className={`${styles.enquireButton} ${isBoiWebsite ? 'light-blue-button-boi' : 'blue-button'}`} 
            onClick={() => {
              event('clickedOnEnquireButtonEvComparisonPage')
              openInNewTab(url)
            }}
          >
            Test Drive / Enquire
          </button>
        )
        break;
      
      case 'business':
        value = '-';
        if (!!vehicle?.lease_data?.business?.monthly_net) {
          value = formatCurrency(vehicle.lease_data.business?.monthly_net)
          value += ' per Month excl. VAT*'
        }
        break;

      default:
        value = vehicle[key];
    }

    return value
  }

  const renderCell = (vehicle: any, key: string, id: string, best: boolean) => {
    const value = getValue(vehicle, key);

    return <div key={id} className={styles.cell + " " + (best ? isBoiWebsite ? styles.highlightBoi : styles.highlight : "")}>{ value }</div>
  }
  const renderEmptyCell = (id: string) => {
    return <div key={id} className={styles.cell}>-</div>
  }

  const renderTable = (rows: any) => {
    return rows.map((row: any) => {
      return (
        <div className={styles.tableRow} key={row.key}>

          <div className={styles.rowLabel}>
            {row.label}
            {row.subHeader && (
              <>
                <br/>{row.subHeader}
              </>
            )}
          </div>
          
          <div className={styles.fieldsRow}>
            { all_vehicles.map((vehicle, index) => {
              const id = `${row.key}-${index}`;

              // if this is an empty vehicle
              if (Object.keys(vehicle).length === 0) {
                return renderEmptyCell(id)
              }
              else {
                let bestIndex = -1;
                // looking for the best value in the Row
                if (row.compareFunction) {
                  bestIndex = row.compareFunction(all_vehicles, row.key)
                }
                return renderCell(vehicle, row.key, id, index === bestIndex)
              }
            })}
          </div>
        </div>
      )
    })
  }

  return (
    <div className={styles.comparisonTable}>
      { renderTable(tableRows) }
    </div>
  )
};
